import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';

/**
 * Column metadata for the Workspace dynamic table (per
 * WORKSPACE_IMPLEMENTATION_PLAN.md section 4). This is CLIENT-SIDE ONLY —
 * the Workspace API has no column-CRUD endpoint, so add/remove/reorder/hide/
 * resize/rename never leave the browser today. Kept in Redux (rather than
 * component state) so it's a one-line swap to real persistence once the
 * backend adds it: dispatch a thunk from these same actions instead of
 * mutating local state.
 */

export type WorkspaceColumnType = 'text' | 'email' | 'phone' | 'link' | 'number';

export interface WorkspaceColumn {
  id: string;
  /** The key this column reads/writes on a row's `input` object. */
  key: string;
  name: string;
  type: WorkspaceColumnType;
  width: number;
  isHidden: boolean;
}

interface WorkspaceColumnsState {
  byId: Record<string, WorkspaceColumn>;
  allIds: string[];
  selectedColumnId: string | null;
  /** Keys the user explicitly deleted, so bootstrapColumnsFromKeys doesn't
   * resurrect them the next time rows are refetched (pagination, manual
   * refresh, post-enrichment refetch all re-run bootstrap with the same
   * row keys). Cleared on resetColumns (sheet switch). */
  removedKeys: Record<string, true>;
}

const initialState: WorkspaceColumnsState = {
  byId: {},
  allIds: [],
  selectedColumnId: null,
  removedKeys: {},
};

/**
 * Starting width per type, since the values these hold are wildly different
 * lengths. A LinkedIn URL — which is what a `link` column almost always is
 * here, and what the matching column always is — needs roughly twice the
 * room of a job title, and at the old flat 160 it truncated on every row
 * while the header had the "Matching" badge and the column menu to fit as
 * well. Numbers get less than the default rather than more.
 */
const DEFAULT_WIDTH_BY_TYPE: Record<WorkspaceColumnType, number> = {
  link: 320,
  email: 280,
  phone: 240,
  text: 160,
  number: 120,
};

export function defaultColumnWidth(type: WorkspaceColumnType): number {
  return DEFAULT_WIDTH_BY_TYPE[type] ?? 160;
}

function inferType(key: string): WorkspaceColumnType {
  const k = key.toLowerCase();
  if (k.includes('email')) return 'email';
  if (k.includes('phone') || k.includes('mobile')) return 'phone';
  if (k.includes('linkedin') || k.includes('website') || k.includes('domain') || k.includes('url')) return 'link';
  return 'text';
}

const workspaceColumnsSlice = createSlice({
  name: 'workspaceColumns',
  initialState,
  reducers: {
    /** Bootstraps columns from the keys seen in fetched rows, without
     * clobbering columns the user already customized (renamed/hidden/resized/
     * reordered) — new keys are appended at the end. */
    bootstrapColumnsFromKeys(state, action: PayloadAction<string[]>) {
      for (const key of action.payload) {
        if (state.byId[key] || state.removedKeys[key]) continue;
        const type = inferType(key);
        state.byId[key] = { id: key, key, name: key, type, width: defaultColumnWidth(type), isHidden: false };
        state.allIds.push(key);
      }
    },
    addColumn(state, action: PayloadAction<{ key: string; name: string; type: WorkspaceColumnType }>) {
      const { key, name, type } = action.payload;
      if (state.byId[key]) return;
      state.byId[key] = { id: key, key, name, type, width: defaultColumnWidth(type), isHidden: false };
      state.allIds.push(key);
      delete state.removedKeys[key];
    },
    /**
     * Lets a previously-deleted key be bootstrapped from row data again.
     *
     * `removeColumn` records the key in `removedKeys` so a refetch can't
     * resurrect the column. Bringing a deleted column back (see the Columns
     * popover's "Removed" group) has to lift exactly that, and only that —
     * unlike `addColumn` it must NOT mint a column here, because the real one
     * arrives from the next rows response with its inferred type and the
     * values still stored upstream.
     */
    restoreRemovedKey(state, action: PayloadAction<string>) {
      delete state.removedKeys[action.payload];
    },
    removeColumn(state, action: PayloadAction<string>) {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
      if (state.selectedColumnId === action.payload) state.selectedColumnId = null;
      state.removedKeys[action.payload] = true;
    },
    renameColumn(state, action: PayloadAction<{ id: string; name: string }>) {
      const col = state.byId[action.payload.id];
      if (col) col.name = action.payload.name;
    },
    setColumnType(state, action: PayloadAction<{ id: string; type: WorkspaceColumnType }>) {
      const col = state.byId[action.payload.id];
      if (col) col.type = action.payload.type;
    },
    resizeColumn(state, action: PayloadAction<{ id: string; width: number }>) {
      const col = state.byId[action.payload.id];
      if (col) col.width = Math.max(80, action.payload.width);
    },
    toggleColumnVisibility(state, action: PayloadAction<string>) {
      const col = state.byId[action.payload];
      if (col) col.isHidden = !col.isHidden;
    },
    /**
     * Apply the server's persisted hidden set to columns at load time. The
     * row bootstrap can't tell show/hide (rows only carry visible keys), so
     * without this, columns the service hides are born `isHidden: false` —
     * the popover then shows them checked, "Show all" is disabled, and the
     * next Apply silently resurrects every one of them. Only marks columns
     * that exist; keys with no column are ignored (they're deleted columns,
     * handled by `removedColumns`).
     */
    syncHiddenColumns(state, action: PayloadAction<string[]>) {
      const hidden = new Set(action.payload);
      for (const id of state.allIds) {
        const col = state.byId[id];
        if (col) col.isHidden = hidden.has(id);
      }
    },
    reorderColumns(state, action: PayloadAction<string[]>) {
      state.allIds = action.payload;
    },
    moveColumn(state, action: PayloadAction<{ id: string; direction: 'left' | 'right' }>) {
      const { id, direction } = action.payload;
      const index = state.allIds.indexOf(id);
      if (index === -1) return;
      const swapWith = direction === 'left' ? index - 1 : index + 1;
      if (swapWith < 0 || swapWith >= state.allIds.length) return;
      [state.allIds[index], state.allIds[swapWith]] = [state.allIds[swapWith], state.allIds[index]];
    },
    setSelectedColumn(state, action: PayloadAction<string | null>) {
      state.selectedColumnId = action.payload;
    },
    resetColumns(state) {
      state.byId = {};
      state.allIds = [];
      state.selectedColumnId = null;
      state.removedKeys = {};
    },
  },
});

export const {
  bootstrapColumnsFromKeys,
  addColumn,
  restoreRemovedKey,
  removeColumn,
  renameColumn,
  setColumnType,
  resizeColumn,
  toggleColumnVisibility,
  syncHiddenColumns,
  reorderColumns,
  moveColumn,
  setSelectedColumn,
  resetColumns,
} = workspaceColumnsSlice.actions;

/** Memoized with createSelector — see selectWorkspaceRows in workspaceDataSlice.ts for why a plain .map().filter() here is unsafe. */
export const selectAllWorkspaceColumns = createSelector(
  [(state: RootState) => state.workspaceColumns.byId, (state: RootState) => state.workspaceColumns.allIds],
  (byId, allIds): WorkspaceColumn[] => allIds.map((id: string) => byId[id]).filter(Boolean)
);

export const selectVisibleWorkspaceColumns = createSelector(
  [selectAllWorkspaceColumns],
  (columns): WorkspaceColumn[] => columns.filter((c) => !c.isHidden)
);

export default workspaceColumnsSlice.reducer;
